import axios from 'axios';

import type {
  PromotionRequestInfo,
  RollbackPreviewResult,
  RollbackPromotionParams,
} from '~/server_shared/openapi/data-contracts';
import { IncomingPromotionListResultPromotionOrigin } from '~/server_shared/openapi/data-contracts';
import { prettifyKey } from '~/Utils';

import type {
  ChangeGroup,
  IncomingPromotionRow,
  PromotionChange,
  PromotionConflict,
  PromotionTypeCounts,
} from './types';
import { PROMOTION_KIND, PROMOTION_STATUS } from './types';

/** Deployment sub-domain → friendly environment label. Encodes backend-only knowledge — `staging` is the
 * sandbox deployment, and production regions are the bare region codes — so it lives in exactly one place.
 * An unknown / newly-added deployment falls back to the raw sub-domain (forward-compatible). */
const DEPLOYMENT_ENV_LABELS: Record<string, string> = {
  dev: 'Dev',
  demo: 'Demo',
  staging: 'Sandbox',
  uat: 'UAT',
  'poc-uk': 'POC (UK)',
  'uat-au': 'UAT (AU)',
  'xc-si': 'XC-SI',
  us: 'Prod (US)',
  eu: 'Prod (EU)',
  uk: 'Prod (UK)',
  au: 'Prod (AU)',
};

/** The friendly, human-readable label for a deployment sub-domain (e.g. `staging` → "Sandbox"), falling
 * back to the raw value for an unknown/new deployment. The single source of truth for env labels, used by
 * {@link PromotionEnvBadge}. */
export function deploymentEnvLabel(subDomain: string): string {
  return DEPLOYMENT_ENV_LABELS[subDomain] ?? subDomain;
}

/** A cross-deployment target failure. The source-side promotion routes (preview/create/cancel/refresh)
 * map every RemoteTransport failure to HTTP 502 carrying `error_code: 'TARGET_UNREACHABLE'` — the target
 * deployment is unreachable, returned a non-2xx, or rejected the signature. Distinct from a domain error
 * (4xx the user must fix): the operator can't act on a deployment that's down, so the UI shows a
 * non-blocking, retryable notice rather than the generic error modal, and does not report it to Sentry (a
 * transiently-down env is expected, not a bug). We require the marker so an infra/gateway 502 (e.g. an
 * ingress/LB in front of the source app itself) — which has no such body — still surfaces the error modal
 * and Sentry rather than being silently swallowed. */
export function isTargetUnreachableError(error: unknown): boolean {
  // `error !== null` guard is required: our axios's isAxiosError does `typeof payload === 'object' &&
  // payload.isAxiosError` and `typeof null === 'object'`, so it throws on null rather than returning false.
  return (
    error !== null &&
    axios.isAxiosError(error) &&
    error.response?.status === 502 &&
    (error.response.data as { error_code?: string } | undefined)?.error_code === 'TARGET_UNREACHABLE'
  );
}

export const TARGET_UNREACHABLE_MESSAGE =
  'Couldn’t reach the target environment — it may be temporarily unavailable. Please try again.';

/** Shared shape of the actionable approve/resolve/preview 422s below: an operator-fixable, retryable domain
 * error a promotion route surfaces with a stable `error_code` and an actionable `message`. Returns the
 * server's message (or `fallback` when it's missing/blank) when the error is a 422 carrying `errorCode`,
 * else null. */
function actionable422Message(error: unknown, errorCode: string, fallback: string): string | null {
  // `error !== null` guard is required: our axios's isAxiosError does `typeof payload === 'object' &&
  // payload.isAxiosError` and `typeof null === 'object'`, so it throws on null rather than returning false.
  if (
    error !== null &&
    axios.isAxiosError(error) &&
    error.response?.status === 422 &&
    (error.response.data as { error_code?: string } | undefined)?.error_code === errorCode
  ) {
    const message = (error.response.data as { message?: string } | undefined)?.message;
    return message && message.trim().length > 0 ? message : fallback;
  }
  return null;
}

/** Shared shape of the marked 422s that list what they refuse: the server's summary `message` (or `fallback` when
 * it's missing/blank) and one `reasons` entry per offending item, when the error is a 422 carrying `errorCode`,
 * else null. */
function actionable422Reasons(
  error: unknown,
  errorCode: string,
  fallback: string
): { message: string; reasons: string[] } | null {
  // `error !== null` guard: our axios's isAxiosError throws on null (typeof null === 'object').
  if (
    error !== null &&
    axios.isAxiosError(error) &&
    error.response?.status === 422 &&
    (error.response.data as { error_code?: string } | undefined)?.error_code === errorCode
  ) {
    const data = error.response.data as { message?: string; reasons?: unknown };
    // Defensive per-entry filter (not a cast): the body can cross a deployment boundary, so a malformed entry
    // degrades to fewer reasons rather than rendering "[object Object]".
    const reasons = Array.isArray(data.reasons) ? data.reasons.filter((r): r is string => typeof r === 'string') : [];
    const message = data.message?.trim();
    return { message: message || fallback, reasons };
  }
  return null;
}

export const PROMOTION_DEPENDENCY_MISSING_FALLBACK =
  'This promotion references a configuration entity that isn’t present on the target yet. Promote the missing dependency first, then approve again.';

/** A promoted change references a config entity not yet present on the target (a cross-config dependency
 * that hasn't been promoted). The approve route maps this to HTTP 422 carrying
 * `error_code: 'PROMOTION_DEPENDENCY_MISSING'` and an actionable message. Distinct from a bug (generic 5xx)
 * and from a drift conflict (a 200 with a `conflicts` body): it's operator-fixable and retryable, so the UI
 * shows the actionable message in-dialog and keeps the request approvable, without reporting to Sentry.
 * Returns the server's actionable message (or a fallback) when this is that error, else null. */
export function getPromotionDependencyMissingMessage(error: unknown): string | null {
  return actionable422Message(error, 'PROMOTION_DEPENDENCY_MISSING', PROMOTION_DEPENDENCY_MISSING_FALLBACK);
}

export const PROMOTION_CHANGE_REJECTED_FALLBACK =
  'The target rejected one of the promoted changes as unsupported or invalid. Adjust the change on the source, then approve again.';

/** An adapter deliberately rejected a promoted change as unsupported/invalid (e.g. demoting a multi-claim
 * contact role to single-claim, or disabling a system role). The approve route maps this to HTTP 422
 * carrying `error_code: 'PROMOTION_CHANGE_REJECTED'` and an actionable message naming the offending change.
 * Like the dependency-missing case it's operator-fixable and retryable — the UI shows the message in-dialog
 * and keeps the request approvable, without reporting to Sentry. Returns the server's message (or a
 * fallback) when this is that error, else null. */
export function getPromotionChangeRejectedMessage(error: unknown): string | null {
  return actionable422Message(error, 'PROMOTION_CHANGE_REJECTED', PROMOTION_CHANGE_REJECTED_FALLBACK);
}

export const PROMOTION_CHANGE_LIST_UNAPPLICABLE_FALLBACK =
  'This promotion was previewed before a configuration update and can no longer be applied as-is. Re-run the preview to continue.';

/** The reviewed change list can no longer be applied as-is — it was previewed before a configuration-format
 * migration, or a stored element is malformed. The approve route maps this to HTTP 422 carrying
 * `error_code: 'PROMOTION_CHANGE_LIST_UNAPPLICABLE'` and an actionable "re-preview" message. Like the other
 * approve 422s it's operator-fixable and retryable — the UI shows the message in-dialog and keeps the
 * request approvable, without reporting to Sentry. Returns the server's message (or a fallback) when this is
 * that error, else null. */
export function getPromotionChangeListUnapplicableMessage(error: unknown): string | null {
  return actionable422Message(error, 'PROMOTION_CHANGE_LIST_UNAPPLICABLE', PROMOTION_CHANGE_LIST_UNAPPLICABLE_FALLBACK);
}

export const PROMOTION_IMMUTABLE_FIELD_CHANGED_TITLE = 'Field can’t be changed by a promotion';

export const PROMOTION_IMMUTABLE_FIELD_CHANGED_FALLBACK =
  'The source sets a field a promotion can’t change differently from the target. Make the source match the target, then preview again.';

/** The source sets a field a promotion can't change (e.g. a coverage's damage assessment type) differently from
 * the target. The preview routes map this to HTTP 422 carrying `error_code: 'PROMOTION_IMMUTABLE_FIELD_CHANGED'`
 * and an actionable message naming the field and each side's value. It's operator-fixable — make the source
 * match the target, then preview again — so the UI shows the message in-dialog, without reporting to Sentry.
 * Returns the server's message (or a fallback) when this is that error, else null. */
export function getPromotionImmutableFieldChangedMessage(error: unknown): string | null {
  return actionable422Message(error, 'PROMOTION_IMMUTABLE_FIELD_CHANGED', PROMOTION_IMMUTABLE_FIELD_CHANGED_FALLBACK);
}

/** A create-time "a pending request already exists for this target" conflict (HTTP 409 carrying
 * `error_code: 'PENDING_PROMOTION_EXISTS'`). Distinct from the other create 409s (preview drift/expiry,
 * wrong pairing, stale selection): those clear by re-previewing, whereas this one only clears when the
 * target approves/rejects the pending request or the source withdraws it — so the UI keeps the user’s
 * previewed/selected work and shows a dedicated inline notice instead of the generic error modal + a
 * destructive preview reset. */
export function isPendingPromotionExistsError(error: unknown): boolean {
  // `error !== null` guard: our axios's isAxiosError throws on null (typeof null === 'object').
  return (
    error !== null &&
    axios.isAxiosError(error) &&
    error.response?.status === 409 &&
    (error.response.data as { error_code?: string } | undefined)?.error_code === 'PENDING_PROMOTION_EXISTS'
  );
}

export const PENDING_PROMOTION_EXISTS_MESSAGE =
  'A promotion to this target is already pending review; it must be approved, rejected, or withdrawn before another can be submitted.';

/** The rollback's acknowledged sections no longer match the sections the target's export is blocked on (none
 * acknowledged, or the blocked set changed since the preview). The rollback route maps this to HTTP 422 carrying
 * `error_code: 'PROMOTION_ROLLBACK_BLOCKED_UNACKNOWLEDGED'`; nothing was applied, so the dialog previews again
 * and asks for a fresh acknowledgment. Not a bug → no error modal / Sentry. */
export function isRollbackBlockedUnacknowledgedError(error: unknown): boolean {
  // `error !== null` guard: our axios's isAxiosError throws on null (typeof null === 'object').
  return (
    error !== null &&
    axios.isAxiosError(error) &&
    error.response?.status === 422 &&
    (error.response.data as { error_code?: string } | undefined)?.error_code ===
      'PROMOTION_ROLLBACK_BLOCKED_UNACKNOWLEDGED'
  );
}

export const PROMOTION_EXPORT_BLOCKED_FALLBACK =
  'This organization’s configuration can’t be exported yet. Fix the items listed below, then try again.';

export interface PromotionExportBlocked {
  message: string;
  reasons: string[];
}

/** The source or target org's own content stops its configuration being exported (e.g. a Metabase dashboard
 * built on a card in a user's personal collection). The preview, approve and rollback routes map this to HTTP
 * 422 carrying `error_code: 'PROMOTION_EXPORT_BLOCKED'`, a summary `message` naming the blocked org and one `reasons`
 * entry per offender (a cross-deployment target's reasons are relayed into the same body, so there is no
 * remote-specific shape). Operator-fixable and retryable, so the UI lists the offenders in-dialog and does
 * not report to Sentry. Returns `{message, reasons}` when the error is that 422, else null. */
export function getPromotionExportBlocked(error: unknown): PromotionExportBlocked | null {
  return actionable422Reasons(error, 'PROMOTION_EXPORT_BLOCKED', PROMOTION_EXPORT_BLOCKED_FALLBACK);
}

export const PROMOTION_DANGLING_REFERENCE_TITLE = 'Changes name items the target doesn’t have';

export const PROMOTION_DANGLING_REFERENCE_FALLBACK =
  'Some changes name items the target organization doesn’t have and the promotion doesn’t create. Create each item in the same promotion, or point the field at an item the target has, then preview again.';

export interface PromotionDanglingReferences {
  message: string;
  reasons: string[];
}

/** Changes of the preview name items the target organization doesn't have, through a field that holds another
 * collection's item keys (e.g. an org unit's parent unit), and the promotion doesn't create those items. The preview
 * routes map this to HTTP 422 carrying `error_code: 'PROMOTION_DANGLING_REFERENCE'`, a summary `message` and one
 * `reasons` entry per reference naming the change, the field and the key. Operator-fixable, so the UI lists them
 * in-dialog and does not report to Sentry. Returns `{message, reasons}` when the error is that 422, else null. */
export function getPromotionDanglingReferences(error: unknown): PromotionDanglingReferences | null {
  return actionable422Reasons(error, 'PROMOTION_DANGLING_REFERENCE', PROMOTION_DANGLING_REFERENCE_FALLBACK);
}

export const INVALID_ARTIFACT_FALLBACK =
  'This file isn’t a valid configuration artifact. Upload a configuration export ZIP or a single-type config JSON (with its metadata block).';

/** The upload-source preview route rejects a corrupt / schema-invalid / unknown-type artifact with an
 * HTTP 422 (`InvalidArtifactError`). It is operator-fixable (upload a different file), so the upload dialog
 * surfaces it by the drop zone rather than the generic error modal / Sentry. Returns the server's message
 * (or a fallback) when the error is a 422, else null. */
export function getInvalidArtifactMessage(error: unknown): string | null {
  // `error !== null` guard: our axios's isAxiosError throws on null (typeof null === 'object').
  if (error !== null && axios.isAxiosError(error) && error.response?.status === 422) {
    const message = (error.response.data as { message?: string } | undefined)?.message;
    return message && message.trim().length > 0 ? message : INVALID_ARTIFACT_FALLBACK;
  }
  return null;
}

/** Group a flat change list by `configuration_type`, attaching the per-type counts from `summary`. */
export function groupChangesByType(changes: PromotionChange[], summary: PromotionTypeCounts[]): ChangeGroup[] {
  const countsByType = new Map(summary.map((c) => [c.configuration_type, c]));
  const groups = new Map<string, PromotionChange[]>();
  for (const change of changes) {
    const list = groups.get(change.configuration_type) ?? [];
    list.push(change);
    groups.set(change.configuration_type, list);
  }
  return Array.from(groups.keys())
    .sort()
    .map((configuration_type) => ({
      configuration_type,
      counts: countsByType.get(configuration_type),
      changes: groups.get(configuration_type) ?? [],
    }));
}

export function hasCollision(changes: PromotionChange[]): boolean {
  return changes.some((c) => Boolean(c.conflict));
}

/** Whether any change carries pruned scope references — members dropped during cross-org projection because
 * they're absent from the target — driving the aggregate "References pruned" banner. Keyed on pruned members
 * present (not on `warnings`), so the banner co-occurs exactly with the per-change `PrunedReferencesBadge`,
 * which renders off the same predicate. */
export function hasPrunedReferences(changes: PromotionChange[]): boolean {
  return changes.some((c) => (c.pruned?.lobs?.length ?? 0) + (c.pruned?.sub_organizations?.length ?? 0) > 0);
}

/** One field of a conflict, shown three ways: what the promotion expected the target to hold
 * (`expected`, the diff-time pre-image), what it actually holds now (`current`, the drift), and what an
 * override would write (`target`, the promotion's new value). */
export interface ConflictFieldRow {
  key: string;
  expected: unknown;
  current: unknown;
  target: unknown;
}

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Deterministic stringify that sorts object keys at every level, so equality is insensitive to key
 * ordering (the same object serialized at two different moments can otherwise differ). Mirrors the
 * backend hash, which sorts keys. */
function stableStringify(value: unknown): string {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;
    const parts = Object.keys(obj)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(obj[key])}`);
    return `{${parts.join(',')}}`;
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  return JSON.stringify(value);
}

function valuesEqual(a: unknown, b: unknown): boolean {
  return stableStringify(a) === stableStringify(b);
}

/** Reduce a conflict to just the fields that differ across the three sides, so the approver sees the
 * decision (which keys drifted / will change) rather than a full serialized object.
 *
 * - `expected` = the conflict's `expected_old` (pre-image the promotion assumed)
 * - `current`  = the conflict's `actual_current` (the target's current, drifted value)
 * - `target`   = the change's `after_value` (what an override would write)
 *
 * When any side is an object, returns one row per key where the three are not all equal (possibly empty
 * if they are deeply equal). With no pre-image at all (a CREATE collision: the target had no object at
 * preview, so `expected` is null), there is nothing to have drifted from — only keys where the target's
 * object differs from the incoming one are relevant, and keys identical on both sides are dropped rather
 * than reported as drift. When all sides are scalars/arrays, returns a single whole-value row
 * (`key: ''`). */
export function conflictDiffRows(expected: unknown, current: unknown, target: unknown): ConflictFieldRow[] {
  if (isPlainObject(expected) || isPlainObject(current) || isPlainObject(target)) {
    const e = isPlainObject(expected) ? expected : {};
    const c = isPlainObject(current) ? current : {};
    const t = isPlainObject(target) ? target : {};
    const keys = Array.from(new Set([...Object.keys(e), ...Object.keys(c), ...Object.keys(t)])).sort();
    const hasPreImage = expected !== null && expected !== undefined;
    return (
      keys
        .map((key) => ({ key, expected: e[key], current: c[key], target: t[key] }))
        // Keep only keys relevant to the decision: an override would change the current value, and/or —
        // when there is a pre-image to compare against — the target drifted from it.
        .filter(
          (row) => !valuesEqual(row.current, row.target) || (hasPreImage && !valuesEqual(row.expected, row.current))
        )
    );
  }
  return [{ key: '', expected, current, target }];
}

/** Classify a conflict field row: `drift` when the target's current value diverged from the pre-image the
 * promotion assumed (`expected != current`) — the actual collision the reviewer must adjudicate; `ordinary`
 * when the field only differs because the promotion changes it (`expected == current`, `current != target`)
 * — a routine change riding along in the same whole-object override. */
export function conflictRowKind(row: ConflictFieldRow): 'drift' | 'ordinary' {
  return valuesEqual(row.expected, row.current) ? 'ordinary' : 'drift';
}

/** Whether a detected conflict genuinely needs a human decision. A change is only a *material* conflict
 * when some field both drifted on the target AND would be clobbered by an override — i.e. `expected !=
 * current` (someone changed it) AND `current != new` (the promotion would overwrite that change with a
 * different value). When no field meets both, applying the promotion overwrites nothing the target
 * changed, so the object can apply automatically like any non-conflicting change (e.g. a field that
 * drifted to the very value the promotion also sets: `expected != current` but `current == new`). */
export function isGenuineConflict(conflict: PromotionConflict, afterValue: unknown): boolean {
  return conflictDiffRows(conflict.expected_old, conflict.actual_current, afterValue).some(
    (row) => !valuesEqual(row.expected, row.current) && !valuesEqual(row.current, row.target)
  );
}

/** Whether a reported conflict needs the reviewer's decision (a card with Keep current / Use incoming) or
 * applies automatically as benign. The single source for that split: the render (which conflicts get a
 * card) and the resolve payload (which conflict ids are overridden unconditionally as benign) both call
 * this, so they cannot disagree — a conflict shown with a card but treated as benign by the payload would
 * be overridden against an explicit "Keep current".
 *
 * A REORDER conflict is material unconditionally: the decision is whole-collection, and the live order
 * having drifted to exactly the promoted one is still a decision, not a no-op. A CREATE conflict is
 * material unconditionally and only ever skippable: the target already holds the key, so applying the
 * create would blind-INSERT and collide — `ConflictCard` disables its override option. Everything else
 * is material only when a field both drifted and would be clobbered (`isGenuineConflict`). */
export function isMaterialConflict(change: PromotionChange, conflict: PromotionConflict): boolean {
  return (
    change.change_type === 'REORDER' ||
    change.change_type === 'CREATE' ||
    isGenuineConflict(conflict, change.after_value)
  );
}

export const promotionsBaseRoute = (organizationId: number | string): string =>
  `/sysconfig/api/v1/organizations/${organizationId}/config_promotions`;

/** Whether an incoming promotion was drafted through the configuration MCP (the assistant). */
const isAssistantDraft = (row: { origin?: string | null }): boolean =>
  row.origin === IncomingPromotionListResultPromotionOrigin.McpDraft;

/** The Source label for an incoming promotion. An upload-source promotion rides the synthetic local
 * pairing (source == target == this org), so `source_org_external_id` would just echo the org's own id;
 * show "Uploaded file" instead, or "Assistant draft" for one the assistant drafted that way. A genuine
 * cross-org promotion shows the source org's external id. */
export const incomingSourceLabel = (row: {
  is_upload?: boolean;
  origin?: string | null;
  source_org_external_id: string;
}): string => {
  if (isAssistantDraft(row)) {
    return 'Assistant draft';
  }
  return row.is_upload ? 'Uploaded file' : row.source_org_external_id;
};

/** Who drafted an incoming promotion through the assistant, shown in place of its requester: staff act as the
 * organization's System user, so the requester names only that user. Null for any other promotion. */
export const assistantDraftedByLabel = (row: {
  origin?: string | null;
  drafted_by_display_name?: string | null;
}): string | null => {
  if (!isAssistantDraft(row)) {
    return null;
  }
  return row.drafted_by_display_name
    ? `Drafted by ${row.drafted_by_display_name} via the assistant`
    : 'Drafted via the assistant';
};

/** The head deployment: the single most-recent DEPLOYED row across ALL kinds (promotion or rollback), or null
 * when nothing is deployed. Mirrors the backend's `_latest_deployment`. */
function latestDeployedRow(rows: IncomingPromotionRow[]): IncomingPromotionRow | null {
  let head: IncomingPromotionRow | null = null;
  let headTime = -Infinity;
  for (const row of rows) {
    if (row.status !== PROMOTION_STATUS.DEPLOYED) continue;
    // A DEPLOYED row always carries a resolved_at; fall back to created_at defensively.
    const time = new Date(row.resolved_at ?? row.created_at).getTime();
    if (head === null || time > headTime) {
      head = row;
      headTime = time;
    }
  }
  return head;
}

/** The `request_uuid` of the head deployment (see `latestDeployedRow`). Only the head is a rollback candidate —
 * mirrors the backend's `_is_latest_deployment`, which picks the newest DEPLOYED request (by resolved time)
 * regardless of kind, so a rollback layered on top disqualifies re-rolling the original beneath it. Returns
 * null when nothing is deployed. */
export function latestDeployedRequestUuid(rows: IncomingPromotionRow[]): string | null {
  return latestDeployedRow(rows)?.request_uuid ?? null;
}

/** The sections a second rollback of `row` would restore: `row` is a ROLLED_BACK promotion and the latest
 * deployment for the target is its rollback, which left those sections out because the target's export of
 * them was blocked. Empty otherwise. Mirrors the backend's `_rollback_scope` second pass. */
export function deferredSectionsToRollBack(row: IncomingPromotionRow, rows: IncomingPromotionRow[]): string[] {
  if (row.kind !== PROMOTION_KIND.PROMOTION || row.status !== PROMOTION_STATUS.ROLLED_BACK) {
    return [];
  }
  const head = latestDeployedRow(rows);
  if (head?.kind !== PROMOTION_KIND.ROLLBACK || head.rolls_back_request_uuid !== row.request_uuid) {
    return [];
  }
  return head.deferred_configuration_types ?? [];
}

/** Whether a row should offer Roll back, gating the client-verifiable eligibility rules the backend's
 * `_rollback_scope` enforces: it must be a **promotion** row (a rollback row is never rollbackable) that is
 * either DEPLOYED and the single latest deployment for the target, or ROLLED_BACK by a latest rollback that
 * deferred blocked sections (the second pass, see `deferredSectionsToRollBack`). The remaining rules (both
 * snapshots present, no un-appliable drift, the deferred sections no longer blocked) can't be checked on the FE
 * and are deferred to the preview's `rollbackable: false` + `blocked_reason`. */
export function isRollbackable(row: IncomingPromotionRow, rows: IncomingPromotionRow[]): boolean {
  if (row.kind !== PROMOTION_KIND.PROMOTION) {
    return false;
  }
  if (row.status === PROMOTION_STATUS.DEPLOYED) {
    return row.request_uuid === latestDeployedRequestUuid(rows);
  }
  return deferredSectionsToRollBack(row, rows).length > 0;
}

/** Preview rolling back a deployed promotion: the read-only restore change list plus any post-deploy drift
 * surfaced as conflicts, or `rollbackable: false` + `blocked_reason` when the promotion can't be rolled back
 * (not the latest deployment, not deployed, missing a snapshot, or an un-appliable restore). */
export async function previewRollback(baseRoute: string, requestUuid: string): Promise<RollbackPreviewResult> {
  const { data } = await axios.post<RollbackPreviewResult>(`${baseRoute}/rollback/preview`, {
    request_uuid: requestUuid,
  });
  return data;
}

/** Roll back a deployed promotion, restoring its touched objects to their pre-apply values. A drift is
 * returned in-band as a `PromotionRequestInfo` with a populated `conflicts` body (nothing applied) — resolve
 * it and re-submit with `override_change_ids` + `expected_conflict_hash`, exactly like approve. */
export async function rollbackPromotion(
  baseRoute: string,
  body: RollbackPromotionParams
): Promise<PromotionRequestInfo> {
  const { data } = await axios.post<PromotionRequestInfo>(`${baseRoute}/rollback`, body);
  return data;
}

export function fieldPathString(change: PromotionChange, key: string): string | undefined {
  const value = change.field_path?.[key];

  return typeof value === 'string' ? value : undefined;
}

/** The object a change targets — built from the structured `field_path` (mirrors the Audit Log's
 * Object column): each path segment prettified, joined with " → ". When the backend provides a human
 * label (`field_path.display_label`, sourced from the export schema's `__label_key__`), it replaces the
 * trailing identity-key segment so entities read as "Exposure Rules → My Rule" / "Templates → Welcome
 * Email" instead of the prettified sync-ULID. The label is whatever the backend composed — e.g. a
 * generic template folds its immutable type into the label ("Templates › Email › Welcome Email") so
 * same-named templates of different types are distinguishable — so this stays adapter-agnostic and just
 * renders it. Falls back to the prettified path / object_desc / id. */
export function objectLabel(change: PromotionChange): string {
  const displayLabel = change.field_path?.display_label;
  const path = change.field_path?.path;
  if (path) {
    // A collection item's path is its collection path plus its identity key, and the key can itself contain
    // '.' (a Metabase report's key is its name path), so the key stays the one segment the label replaces.
    const parentPath = fieldPathString(change, 'parent_path');
    const fieldId = fieldPathString(change, 'field_id');
    const rawSegments =
      displayLabel && parentPath && fieldId && path === `${parentPath}.${fieldId}`
        ? [...parentPath.split('.'), fieldId]
        : path.split('.');
    // A `__order__` path segment is a sentinel marking a collection's ordering, not a real field/object
    // name — mapped to a fixed human label explicitly, detected on the raw segment (before prettifyKey),
    // rather than relying on prettifyKey's generic word-casing behavior to happen to read well for it.
    const segments = rawSegments.map((segment) => (segment === '__order__' ? 'Order' : prettifyKey(segment)));
    if (displayLabel && segments.length > 0) {
      segments[segments.length - 1] = displayLabel;
    }
    return segments.join(' › ');
  }
  return displayLabel || change.object_desc || change.change_id;
}

/** Matches only well-formed HTML markup so `formatValue` strips leaked template tags without touching
 * comparison expressions. A tag is `<name>` / `</name>` / `<name/>` for a known HTML tag name, optionally
 * followed by attributes — but only attributes that carry an `=` (real HTML: `class="x"`, `href='y'`).
 * That `=` requirement is what keeps identifier comparisons intact: `if tier<gold and x>3` and `a<b and c>d`
 * have no `=` between the operators, so neither `<gold and x>` nor `<b and c>` (even though `b` is a tag
 * name) is treated as a tag. Numeric comparisons (`if x<5`) already fail the tag-name check. */
const HTML_TAG_RE =
  /<\/?(?:a|b|blockquote|br|code|div|em|font|h[1-6]|hr|i|img|li|ol|p|pre|span|strong|table|tbody|td|th|thead|tr|u|ul)(?:\s*\/?|\s+[^>]*=[^>]*)>/gi;

/** Compact, readable rendering of a value: serialize non-strings, then strip the internal shape a
 * promotion payload leaks — JSON-escaped quotes, embedded HTML/markup tags, and collapsed whitespace —
 * so a serialized template/config reads as plain text rather than source. Truncated (a created object can
 * be a large dict). */
export function formatValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '∅';
  }
  // Indent of 1, then collapse whitespace: the net effect is a space after each colon and comma, so a
  // serialized object reads as `{ "a": 1, "b": 2 }` and can wrap at those spaces instead of running as one token.
  const text = (typeof value === 'string' ? value : JSON.stringify(value, null, 1))
    .replace(/\\"/g, '"') // JSON-escaped quotes → plain quotes
    .replace(HTML_TAG_RE, ' ') // drop leaked markup tags (see HTML_TAG_RE) — comparison expressions survive
    .replace(/\s+/g, ' ') // collapse whitespace
    .trim();
  return text.length > 140 ? `${text.slice(0, 137)}…` : text;
}

/** What a REORDER change produces. `order` is the collection's order once applied — `resolved_after_value`,
 * computed server-side by the same function the apply runs, so a member the target has and the source does
 * not is kept at its own index rather than reading as deleted. Changes stored before that field existed
 * have it empty and fall back to `after_value`, the promoted order. `promotedKeys` (the keys the promotion
 * actually names) is set only when a resolved order exists — the only case in which "kept in place"
 * (present in the result, never named by the promotion) can be told apart. */
export function reorderResult(change: PromotionChange): { order: string[]; promotedKeys?: string[] } {
  const promoted = Array.isArray(change.after_value) ? (change.after_value as string[]) : [];
  const resolved = Array.isArray(change.resolved_after_value) ? (change.resolved_after_value as string[]) : [];
  if (resolved.length === 0) {
    return { order: promoted, promotedKeys: undefined };
  }
  return { order: resolved, promotedKeys: promoted };
}
